import { db } from '../config/firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

// ── Central registry of every draftable form type ──────────────────────
export const DRAFT_TYPES = {
  student: {
    label: 'Student', icon: 'students', color: '#14452F',
    route: '/staff/add-student',
    requiredFields: ['firstName', 'lastName', 'dateOfBirth', 'gender', 'class', 'school', 'region', 'district', 'parentName', 'parentPhone', 'teacherName', 'hasBirthCertificate'],
    previewFields: ['firstName', 'class', 'school', 'region', 'parentName', 'teacherName'],
  },
  parent: {
    label: 'Parent', icon: 'parents', color: '#F57C00',
    route: '/staff/add-parent',
    requiredFields: ['firstName', 'phone', 'region', 'storyBefore'],
    previewFields: ['firstName', 'phone', 'region', 'storyBefore'],
  },
  report: {
    label: 'Report', icon: 'report', color: '#4527A0',
    route: '/programs',
    requiredFields: ['title', 'personName', 'description'],
    previewFields: ['title', 'personName', 'reportType'],
  },
  session: {
    label: 'Session', icon: 'calendar', color: '#00695C',
    route: '/programs',
    requiredFields: ['date', 'topic', 'facilitator', 'notes'],
    previewFields: ['date', 'topic', 'location'],
  },
  meeting: {
    label: 'Meeting Minutes', icon: 'notes', color: '#0277BD',
    route: '/staff/meetings',
    requiredFields: ['title', 'date', 'minutesText'],
    previewFields: ['title', 'date', 'attendees'],
  },
};

export function getDraftConfig(type) {
  return DRAFT_TYPES[type] || DRAFT_TYPES.student;
}

/**
 * Strip anything Firestore will reject (undefined values, raw File objects,
 * functions) before writing. This is the #1 cause of "draft imekataa":
 * Firestore throws if ANY field in the object is `undefined`.
 */
function sanitizeForFirestore(data) {
  const clean = {};
  Object.keys(data || {}).forEach((key) => {
    const value = data[key];
    if (value === undefined) return;
    if (typeof File !== 'undefined' && value instanceof File) return;
    if (typeof value === 'function') return;
    clean[key] = value;
  });
  return clean;
}

export function calculateDraftCompletion(type, data) {
  const cfg = getDraftConfig(type);
  const filled = cfg.requiredFields.filter(f => data && data[f]).length;
  return Math.round((filled / cfg.requiredFields.length) * 100);
}

/**
 * Save (or update) a draft of ANY form type. Safe to call repeatedly —
 * uses merge so partial saves never wipe out unrelated fields.
 */
export async function saveDraft({ type, data, currentUser, draftId }) {
  if (!currentUser || !currentUser.uid) {
    throw new Error('Huwezi kusave draft bila kuingia kwenye account (login).');
  }
  const id = draftId || `draft_${type}_${Date.now()}`;
  const completion = calculateDraftCompletion(type, data);
  const cleanData = sanitizeForFirestore(data);

  await setDoc(doc(db, 'drafts', id), {
    type,
    data: cleanData,
    completion,
    createdBy: currentUser.uid,
    createdByName: currentUser.displayName || currentUser.email || 'Staff',
    updatedAt: serverTimestamp(),
  }, { merge: true });

  return { id, completion };
}
